import { useState, useEffect } from "react";

/**
 * A hook that returns a debounced version of a value.
 * The debounced value will only update after the specified delay has passed.
 * The hook takes two arguments: the value to debounce and the delay in milliseconds.
 * If the delay is not provided, it defaults to 400 milliseconds.
 * The hook returns the debounced value, which can be used in place of the original value.
 */
export function useDebounce<T>(value: T, delay = 400): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debounced;
}
